/**
 * Template bindings/ files for bootstrap.
 */

import type { ProbeResult } from "./probe.js";

export interface BindingTemplates {
  git: string;
  runtime: string;
  observability: string;
}

export function generateBindingTemplates(probe: ProbeResult): BindingTemplates {
  const scripts = probe.packageJson?.scripts ?? {};
  const gitHooks: string[] = [];

  if (scripts.lint) {
    gitHooks.push(`  pre-commit:
    - command: "npm run lint --silent"
      description: "Lint check before commit"`);
  }

  if (scripts.test) {
    const testCmd = probe.isMonorepo
      ? "npm test --workspaces --if-present"
      : "npm test";
    gitHooks.push(`  pre-push:
    - command: "${testCmd}"
      description: "Run test suite before push"`);
  }

  gitHooks.push(`  post-commit:
    - command: "npx aaac-observ record post-commit"
      description: "Record commit event for observability"`);

  const gitYaml =
    gitHooks.length > 0
      ? `# Git hooks binding — gate commands for .git/hooks/\nhooks:\n${gitHooks.join("\n")}\n`
      : `# Git hooks binding — no npm scripts detected for gates\nhooks:\n  post-commit:\n    - command: "npx aaac-observ record post-commit"\n      description: "Record commit event for observability"\n`;

  const runtimeYaml = `software: aaac-bootstrap
version: 1

guardrail_impl:
  no-force-push:
    checks:
      - matcher:
          type: command_regex
          pattern: "(^|[|;&]\\\\s*)git\\\\s+push\\\\s.*(--force|-f)\\\\b"
        message: "Force push (--force / -f) is forbidden."

  no-verify-blocked:
    checks:
      - matcher:
          type: command_regex
          pattern: "git\\\\s+(commit|push)\\\\b.*--no-verify"
        message: "--no-verify is FORBIDDEN. All hooks MUST run."

  block-credential-leak:
    checks:
      - matcher:
          type: file_glob
          pattern: "**/{.env,.env.*}"
        message: "Writing to .env file is blocked."
      - matcher:
          type: file_glob
          pattern: "**/*.{pem,key,p12,pfx,jks,keystore,secret}"
        message: "Writing to certificate/key file is blocked."

  protect-generated-files:
    checks:
      - matcher:
          type: file_glob
          pattern: "**/*.generated.{ts,js,py,yaml}"
        message: "Generated files must not be hand-edited."
`;

  const observabilityYaml = `# Observability event mapping
event_mapping:
  tool_use:
    span_name: "tool.{tool_name}"
    attributes:
      - tool_name
      - agent_id
      - task_id
  task_complete:
    span_name: "task.{task_id}"
    attributes:
      - task_id
      - agent_id
      - result_type
  workflow_step:
    span_name: "workflow.{workflow_id}.{step_index}"
    attributes:
      - workflow_id
      - step_index
      - task_id
`;

  return { git: gitYaml, runtime: runtimeYaml, observability: observabilityYaml };
}
